import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShifts } from '@/hooks/api/useShifts'
import { useAuthStore } from '@/store/authStore'
import type { ShiftItem } from '@/types/api/shifts'
import ROUTES from '@/utils/constants/routes'
import ClockInReminderModal from '@/components/shift-management/ClockInReminderModal'
import { useDispatcher } from '@/providers/DispatcherProvider'

const TEN_MINUTES_MS = 10 * 60 * 1000
const REMINDER_CHECK_INTERVAL_MS = 30 * 1000
const SHIFT_REFETCH_INTERVAL_MS = 60 * 1000
const DISMISSED_SHIFTS_STORAGE_KEY = 'clock-in-reminder-dismissed'

interface ShiftReminderContextValue {
  nextShift: ShiftItem | null
}

const ShiftReminderContext = createContext<ShiftReminderContextValue>({
  nextShift: null,
})

export const useShiftReminder = () => useContext(ShiftReminderContext)

const parseDate = (value?: string | null) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date
}

const isEligibleShift = (shift: ShiftItem, now: Date) => {
  if (shift.status !== 'awaiting') return false
  if (shift.clock_in_status) return false
  const start = parseDate(shift.from)
  const end = parseDate(shift.to)
  if (!start || !end) return false
  return end.getTime() > now.getTime()
}

const getNextUpcomingShift = (
  shifts: ShiftItem[],
  dismissedShiftIds: Set<string>
) => {
  const now = new Date()
  const eligibleShifts = shifts.filter(
    shift =>
      isEligibleShift(shift, now) && !dismissedShiftIds.has(shift.shift_id)
  )
  if (eligibleShifts.length === 0) return null

  return eligibleShifts.reduce<ShiftItem | null>((earliest, shift) => {
    if (!earliest) return shift
    const earliestStart = parseDate(earliest.from)?.getTime() ?? 0
    const shiftStart = parseDate(shift.from)?.getTime() ?? 0
    return shiftStart < earliestStart ? shift : earliest
  }, null)
}

const shouldShowReminder = (shift: ShiftItem, now: Date) => {
  if (!isEligibleShift(shift, now)) return false
  const start = parseDate(shift.from)
  if (!start) return false
  const reminderWindowStart = start.getTime() - TEN_MINUTES_MS
  return now.getTime() >= reminderWindowStart
}

interface ShiftReminderProviderProps {
  children: React.ReactNode
}

export const ShiftReminderProvider: React.FC<ShiftReminderProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const { pendingShiftDefect, isLoading: isLoadingDispatcher } = useDispatcher()
  const dispatcherId = user?.sub

  const { data: shiftData } = useShifts(
    {
      dispatcher_id: dispatcherId,
      limit: 100,
      sortBy: 'from',
      sortOrder: 'asc',
    },
    {
      enabled: Boolean(isAuthenticated && dispatcherId),
      refetchInterval: isAuthenticated ? SHIFT_REFETCH_INTERVAL_MS : false,
    }
  )

  const shifts = useMemo(() => shiftData?.items ?? [], [shiftData?.items])
  const [nextShift, setNextShift] = useState<ShiftItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dismissedShiftIds, setDismissedShiftIds] = useState<Set<string>>(
    () => new Set()
  )

  const loadDismissedShiftIds = (id?: string) => {
    if (!id) return new Set<string>()
    try {
      const raw = localStorage.getItem(DISMISSED_SHIFTS_STORAGE_KEY)
      if (!raw) return new Set<string>()
      const parsed = JSON.parse(raw) as Record<string, string[]>
      const stored = parsed[id] ?? []
      return new Set(stored)
    } catch (error) {
      console.error('Failed to read dismissed shifts:', error)
      return new Set<string>()
    }
  }

  const persistDismissedShiftIds = (id: string, ids: Set<string>) => {
    try {
      const raw = localStorage.getItem(DISMISSED_SHIFTS_STORAGE_KEY)
      const parsed = raw ? (JSON.parse(raw) as Record<string, string[]>) : {}
      parsed[id] = Array.from(ids)
      localStorage.setItem(DISMISSED_SHIFTS_STORAGE_KEY, JSON.stringify(parsed))
    } catch (error) {
      console.error('Failed to persist dismissed shifts:', error)
    }
  }

  const dismissCurrentShift = () => {
    if (!nextShift?.shift_id || !dispatcherId) {
      setIsModalOpen(false)
      return
    }
    setDismissedShiftIds(prev => {
      const next = new Set(prev)
      next.add(nextShift.shift_id)
      persistDismissedShiftIds(dispatcherId, next)
      const nextUpcoming = getNextUpcomingShift(shifts, next)
      setNextShift(nextUpcoming)
      return next
    })
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setNextShift(null)
      setIsModalOpen(false)
      setDismissedShiftIds(new Set())
      try {
        localStorage.removeItem(DISMISSED_SHIFTS_STORAGE_KEY)
      } catch (error) {
        console.error('Failed to clear dismissed shifts:', error)
      }
      return
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !dispatcherId) return
    setDismissedShiftIds(loadDismissedShiftIds(dispatcherId))
  }, [isAuthenticated, dispatcherId])

  useEffect(() => {
    if (!isAuthenticated) return
    const next = getNextUpcomingShift(shifts, dismissedShiftIds)
    setNextShift(next)
  }, [shifts, isAuthenticated, dismissedShiftIds])

  useEffect(() => {
    if (!isAuthenticated || !nextShift) {
      setIsModalOpen(false)
      return
    }

    const evaluateReminder = () => {
      const now = new Date()
      if (pendingShiftDefect || isLoadingDispatcher) {
        setIsModalOpen(false)
        return
      }
      if (shouldShowReminder(nextShift, now)) {
        setIsModalOpen(true)
      } else if (!isEligibleShift(nextShift, now)) {
        setIsModalOpen(false)
      }
    }

    evaluateReminder()

    const intervalId = window.setInterval(
      evaluateReminder,
      REMINDER_CHECK_INTERVAL_MS
    )
    return () => window.clearInterval(intervalId)
  }, [isAuthenticated, nextShift, pendingShiftDefect, isLoadingDispatcher])

  const handleStartClockIn = () => {
    dismissCurrentShift()
    navigate(ROUTES.shiftManagement, {
      state: { openClockIn: true, shiftId: nextShift?.shift_id },
    })
  }

  const contextValue = useMemo<ShiftReminderContextValue>(
    () => ({
      nextShift,
    }),
    [nextShift]
  )

  return (
    <ShiftReminderContext.Provider value={contextValue}>
      {children}
      {isAuthenticated && !pendingShiftDefect && !isLoadingDispatcher && (
        <ClockInReminderModal
          isOpen={isModalOpen}
          shift={nextShift}
          onClose={dismissCurrentShift}
          onStartClockIn={handleStartClockIn}
        />
      )}
    </ShiftReminderContext.Provider>
  )
}

export default ShiftReminderProvider
